/*
 * Copyright (c) 2025 Ipivot Corporation.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 *  Ipivot Corporation ("Confidential Information").
 */
import 'bootstrap/dist/css/bootstrap.min.css';
import Container from 'react-bootstrap/Container';
import dayjs from 'dayjs'
import { Accordion, Row, Col, Badge } from 'react-bootstrap';
import ProbeDetailCode from './ProbeDetailCode';
import ProbeDetailAi from './ProbeDetailAi';
import ProbeDetailDns from './ProbeDetailDns';
import ProbeDetailTcp from './ProbeDetailTcp';
import ProbeDetailCurl from './ProbeDetailCurl';
import { useNavigate } from 'react-router-dom';
import ProbeHierarchy from './ProbeHierarchy';
import { Table } from 'react-bootstrap';
import ProbeTree from './ProbeTree';
import Button from 'react-bootstrap/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faListUl } from '@fortawesome/free-solid-svg-icons';

 function ProbeDetail({
     probe,
     hierarchyView = 'vis',
     showHierarchyToggle = false,
     onToggleHierarchyView,
     lastActiveId = null,
 } : {
     probe: any;
     hierarchyView?: 'vis' | 'tree';
     showHierarchyToggle?: boolean;
     onToggleHierarchyView?: () => void;
     lastActiveId?: number | null;
 }) {
    if (!probe.probe_id) {
        return null;
    }
    const detail = probe.detail || {};
    let dcomp = null;
    switch (probe.probe_type) {
    case 'dns':
        dcomp = <ProbeDetailDns data={probe} />;
        break;
    case 'tcp':
        dcomp = <ProbeDetailTcp data={probe} />;
        break;
    case 'curl':
        dcomp = <ProbeDetailCurl data={probe} />;
        break;
    case 'code':
        dcomp = <ProbeDetailCode data={probe} />;
        break;
    case 'ai':
        dcomp = <ProbeDetailAi data={probe} />;
        break;
    default:
        dcomp = <div>Unknown probe type: {probe.probe_type}</div>;
    }
    const fid = probe.finger_id || detail?.finger_id || 'N/A';
    const navigate = useNavigate();
    const children = Array.isArray(probe.children) ? probe.children : [];
    const rootId = children.length > 0
        ? children.reduce((min: number, c: any) => (c.id < min ? c.id : min), children[0].id)
        : probe.probe_id;
    const forests = new Map<number, any[]>([[rootId, children]]);
    const activeId = lastActiveId || (children.find((c: any) => c.running)?.id ?? null);

    return (
        <Container fluid className="p-4">
            <Row>
                <Col className="col-9">
                    <div style={{ height: '28em', position: 'relative' }}>
                        {showHierarchyToggle && onToggleHierarchyView ? (
                            <Button
                                size="sm"
                                variant="dark"
                                onClick={onToggleHierarchyView}
                                title={hierarchyView === 'vis' ? 'Switch to ProbeTree view' : 'Switch to VisNet view'}
                                style={{
                                    position: 'absolute',
                                    top: 8,
                                    right: 8,
                                    zIndex: 5,
                                    padding: '0.2rem 0.35rem',
                                    opacity: 0.85,
                                }}
                            >
                                <FontAwesomeIcon icon={faListUl} />
                            </Button>
                        ) : null}
                        {hierarchyView === 'tree' ? (
                            <ProbeTree forests={forests} lastActiveId={activeId} />
                        ) : (
                            <ProbeHierarchy children={children} />
                        )}
                    </div>
                </Col>
                <Col className="col-3">
                    <Table borderless size="sm" hover>
                        <tbody>
                            <tr>
                                <td className="text-end pe-2">Probe ID</td>
                                <td>
                                    <span onClick={(e) => probe.probe_id != 'N/A' &&  navigate(`/probe/${probe.probe_id}`)}>
                                        <strong>{probe.probe_id}</strong>
                                    </span>
                                </td>
                            </tr>
                            <tr onClick={(e) => fid !== 'N/A' && navigate(`/fingerprint/${fid}`)}>
                                <td className="text-end pe-2">Fingerprint ID</td>
                                <td>
                                    <span>
                                        <strong>{fid}</strong>
                                    </span>
                                </td>
                            </tr>
                            <tr onClick={(e) => detail.parent_id != 'N/A' && navigate(`/probe/${detail.parent_id}`)}>
                                <td className="text-end pe-2">Parent ID</td>
                                <td>
                                    {detail.parent_id ? (
                                        <span> 
                                            <strong>{detail.parent_id}</strong>
                                        </span>
                                    ) : (
                                        <strong>N/A</strong>
                                    )}
                                </td>
                            </tr>
                            <tr>
                                <td className="text-end pe-2">Type</td>
                                <td><strong>{probe.probe_type}</strong></td>
                            </tr>
                            <tr>
                                <td className="text-end pe-2">Pending</td>
                                <td><strong>{probe.pending}</strong></td>
                            </tr>
                            <tr>
                                <td className="text-end pe-2">Passed</td>
                                <td><strong>{probe.passed}</strong></td>
                            </tr>
                            <tr>
                                <td className="text-end pe-2">Queued</td>
                                <td>{dayjs(probe.queued_ts * 1000).format('YYYY-MM-DD HH:mm:ss')}</td>
                            </tr>
                            <tr>
                                <td className="text-end pe-2">Ran</td>
                                <td>
                                    {(probe.pending || probe.run_ts == 0) ? (
                                        <Badge pill bg="warning">QUEUED</Badge>
                                    ) : (
                                        <span>{dayjs(probe.run_ts * 1000).format('YYYY-MM-DD HH:mm:ss')}</span>
                                    )}
                                </td>
                            </tr>
                        </tbody>
                    </Table>
                </Col>
            </Row>
            { Array.isArray(detail?.log) && detail.log.length > 0 && (
                <Row className="mt-2">
                    <Col>
                        <Accordion flush style={{ }} defaultActiveKey="0">
                            <Accordion.Item eventKey="1">
                                <Accordion.Header>
                                    <span className="fst-bold">Log:&nbsp;</span> 
                                    <span className="fst-italic" style={{ color: "white"}}>...{String(detail.log[detail.log.length - 1])}</span>
                                </Accordion.Header>
                                <Accordion.Body>
                                    <div style={{ padding: '10px', borderRadius: '5px', maxHeight: '800px', overflowY: 'auto', fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                                        {detail.log.map((line: any) => String(line)).join('\n')}
                                    </div>
                                </Accordion.Body>
                            </Accordion.Item>
                        </Accordion>
                    </Col>
                </Row>
            )}
            <Row className="mt-2">
                <Col>
                    {dcomp}
                </Col>
            </Row>
        </Container>
    );
 }

 export default ProbeDetail;